import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { formatCurrency, formatDate, formatTime } from '../../../../../helper/PaymentUtils';
import { ActionButton } from '../ActionButton/ActionButton';
import type { ProcessedPayment } from '../PaymentsTable';
import { StatusBadge } from '../StatusBadge/StatusBadge';

export interface DesktopTableRowProps {
  payment: ProcessedPayment;
  isPolling: boolean;
}

export const DesktopTableRow: React.FC<DesktopTableRowProps> = ({ payment, isPolling }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => setIsOpen(!isOpen);

  const appointment = payment.appointment;
  const hasAppointmentDetails = !!appointment;

  const needsAction = payment.displayStatus === 'Unpaid' && !isPolling;

  const baseRowClass = isPolling
    ? 'bg-blue-50 group-hover:bg-blue-100 transition-colors duration-150'
    : needsAction
      ? 'bg-orange-50 group-hover:bg-orange-100 transition-colors duration-150'
      : 'group-hover:bg-gray-50 transition-colors duration-150';

  const clickableClass = hasAppointmentDetails && !isPolling ? 'cursor-pointer' : '';

  return (
    <React.Fragment>
      <tr
        className={`${baseRowClass} ${clickableClass} group`}
        onClick={!isPolling ? toggleOpen : undefined}
        aria-expanded={hasAppointmentDetails ? isOpen : undefined}
        aria-controls={hasAppointmentDetails ? `details-${payment.id}` : undefined}
      >
        <td className='px-6 py-4 text-sm text-gray-900 align-middle min-w-[250px]'>
          <div className='flex items-center min-h-10'>
            <div className='flex-grow'>
              <div className='font-medium'>{payment.productLabel || `ID: ${payment.id}`}</div>
            </div>
            {hasAppointmentDetails && (
              <div className='ml-2 flex-shrink-0 h-4 w-4 flex items-center justify-center'>
                {!isPolling &&
                  (isOpen ? (
                    <ChevronUp className='h-4 w-4 text-gray-500' />
                  ) : (
                    <ChevronDown className='h-4 w-4 text-gray-500' />
                  ))}
              </div>
            )}
          </div>
        </td>
        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-middle'>
          {isPolling ? (
            <div className='flex items-center gap-2 text-sm text-blue-800 font-medium'>
              <Loader2 className='w-4 h-4 animate-spin' />
              <span>Wird verarbeitet...</span>
            </div>
          ) : (
            <StatusBadge status={payment.displayStatus} />
          )}
        </td>
        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-middle'>
          {formatDate(payment.displayStartedAt)}
        </td>
        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-middle'>
          {formatCurrency(payment.displayAmount)}
        </td>
        <td className='px-6 py-4 whitespace-nowrap text-sm font-medium align-middle'>
          {isPolling ? <div className='h-9' /> : <ActionButton payment={payment} />}
        </td>
      </tr>

      {isOpen && hasAppointmentDetails && !isPolling && (
        <tr id={`details-${payment.id}`} className={needsAction ? 'bg-orange-50' : 'bg-white'}>
          <td colSpan={5} className='px-6 py-4 border-t border-gray-200'>
            <div className='pl-2 border-l-2 border-gray-300 ml-1'>
              <div className='text-xs font-medium text-gray-500 mb-1'>Termin Details:</div>
              <div className='text-sm text-gray-700 space-y-1'>
                <div>{`${appointment.doctorTitle || ''} ${appointment.doctorName}`.trim()}</div>
                <div>
                  {formatDate(appointment.startDate)}
                  {` (${formatTime(appointment.startDate)} - ${formatTime(appointment.endDate)})`}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};
